import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import CaptchaWidget from '../components/CaptchaWidget';
import './Auth.css';

// Handles both halves of the self-service password reset:
//   /forgot-password            → email form, POST /api/users/forgot-password
//   /reset-password?token=...   → new-password form, POST /api/users/reset-password
const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);

  const requestLink = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch('/api/users/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, captcha_token: captchaToken || undefined })
      });
      if (response.ok) {
        setSent(true);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch('/api/users/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        navigate('/login', { state: { notice: 'Password updated — sign in with your new password.' } });
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (token) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1>Set a new password</h1>
          <p className="auth-subtitle">Choose a new password for your account</p>

          {error && (
            <div className="error" role="alert" aria-live="polite">{error}</div>
          )}

          <form onSubmit={submitNewPassword} aria-label="Set new password form">
            <div className="form-group">
              <label htmlFor="password">
                New Password <span className="required" aria-label="required">*</span>
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                required
                minLength={8}
                aria-required="true"
                autoComplete="new-password"
                autoFocus
              />
              <small>At least 8 characters</small>
            </div>

            <div className="form-group">
              <label htmlFor="confirm">
                Confirm New Password <span className="required" aria-label="required">*</span>
              </label>
              <input
                type="password"
                id="confirm"
                name="confirm"
                value={confirm}
                onChange={(e) => { setConfirm(e.target.value); setError(''); }}
                required
                minLength={8}
                aria-required="true"
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? 'Saving...' : 'Save new password'}
            </button>
          </form>

          <p className="auth-footer">
            <Link to="/login">Back to sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>Reset your password</h1>
        <p className="auth-subtitle">
          Enter your account email and we'll send you a reset link
        </p>

        {error && (
          <div className="error" role="alert" aria-live="polite">{error}</div>
        )}

        {sent ? (
          <div className="success" role="status" aria-live="polite">
            If an account exists for that email, a password reset link is on its way.
            The link expires in 1 hour — check your spam folder if you don't see it.
          </div>
        ) : (
          <form onSubmit={requestLink} aria-label="Request password reset form">
            <div className="form-group">
              <label htmlFor="email">
                Email Address <span className="required" aria-label="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                required
                aria-required="true"
                autoComplete="email"
                autoFocus
              />
            </div>

            <CaptchaWidget onToken={setCaptchaToken} />

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? 'Sending...' : 'Email me a reset link'}
            </button>
          </form>
        )}

        <p className="auth-footer">
          Remembered it? <Link to="/login">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
